import React, { useEffect, useState } from "react";
import { getAllocations } from "../../api/apiGetrequest";
import TableHeader from "../../Components/DataTable/TableHeader";
import ActionModal from "../DriverApp/OrderActionModal";
import ShipmentFormModal from "./AddAllocation";

// Helper function to format date as YYYY-MM-DD
const formatDate = (date) => date.toISOString().split("T")[0];

// Calculate the date 3 days ago from today
const today = new Date();
const threeDaysAgo = new Date(today);
threeDaysAgo.setDate(today.getDate() - 3);

const DeliveryTable = () => {
  const [activeStatus, setActiveStatus] = useState("ALL");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(formatDate(threeDaysAgo)); // Default to 3 days ago
  const [endDate, setEndDate] = useState(formatDate(today)); // Default to today
  const [data, setData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedRow, setSelectedRow] = useState(null);

  const statusTypes = [
    { id: "ALL", text: "ALL" },
    { id: 1, text: "OPEN" },
    { id: 6, text: "COMPLETE" },
  ];

  // Helper function to determine delivery status
  const getDeliveryStatus = (item) => {
    if (!item.OffloadingExitTime) return { id: 1, text: "OPEN" };
    return { id: 6, text: "COMPLETE" };
  };

  // Filter data based on active status
  const filteredData = data?.filter((item) => {
    if (activeStatus === "ALL") return true;
    const status = getDeliveryStatus(item);
    return status.id === activeStatus;
  });

  // Calculate totals
  const calculateTotals = () => {
    return filteredData?.reduce((acc, item) => {
      const distanceToOutlet = calculateDistance(
        item.DepartureMileage,
        item.OutletArrivalMileage
      );
      return {
        claim: (acc.claim || 0) + item.Claim,
        casesFull: (acc.casesFull || 0) + item.CasesFull,
        casesEmpty: (acc.casesEmpty || 0) + item.EmptyReturn,
        fullsReturned: (acc.fullsReturned || 0) + item.FullReturn,
        distanceToOutlet: (acc.distanceToOutlet || 0) + distanceToOutlet,
        fuelIssued: (acc.fuelIssued || 0) + item.FuelIssuedLtrs,
      };
    }, {});
  };

  // Helper function to calculate time difference in hh:mm format
  const calculateTimeDifference = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diff = Math.abs(end - start);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  // Calculate distance between two mileage points
  const calculateDistance = (start, end) => {
    return Math.abs(end - start);
  };

  const fetchData = async () => {
    try {
      const { result } = await getAllocations({
        UserID: 0,
        startDate: startDate,
        enddate: endDate, // Corrected the typo from 'enddate' to 'endDate'
      });
      setData(result);
    } catch (error) {
      console.error("Error fetching allocations:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data]);

  const styles = {
    container: { maxWidth: "1640px", margin: "0 auto", padding: "20px" },
    dateFilter: {
      display: "flex",
      gap: "10px",
      marginBottom: "20px",
      justifyContent: "flex-start",
      float: "right",
    },
    dateInput: {
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #ddd",
      fontSize: "14px",
    },
    tabContainer: {
      display: "flex",
      gap: "8px",
      marginBottom: "20px",
      overflowX: "auto",
      padding: "4px",
      float: "left",
    },
    tab: {
      padding: "8px 16px",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "bold",
      textTransform: "uppercase",
      backgroundColor: "#f4f4f4",
      color: "#666",
      whiteSpace: "nowrap",
    },
    activeTab: {
      backgroundColor: "#1565c0",
      color: "white",
    },
    tableWrapper: {
      width: "100%",
      overflowX: "auto",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "@media (max-width: 768px)": {
        display: "none", // Hide table on small screens
      },
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
      textTransform: "uppercase",
      minWidth: "1200px",
    },
    th: {
      backgroundColor: "#f4f4f4",
      padding: "12px 8px",
      border: "1px solid #ddd",
      textAlign: "left",
      fontWeight: "bold",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "8px",
      border: "1px solid #ddd",
      textAlign: "left",
      whiteSpace: "nowrap",
    },
    totalsRow: {
      backgroundColor: "#f8f9fa",
      fontWeight: "bold",
      borderTop: "2px solid #1565c0",
    },
    status: {
      padding: "4px 8px",
      borderRadius: "4px",
      fontWeight: "bold",
      display: "inline-block",
      whiteSpace: "nowrap",
    },
    statusColors: {
      1: { backgroundColor: "#e3f2fd", color: "#1565c0" },
      2: { backgroundColor: "#fff3e0", color: "#e65100" },
      3: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
      4: { backgroundColor: "#fff3e0", color: "#e65100" },
      5: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
      6: { backgroundColor: "#e0e0e0", color: "#424242" },
    },
    cardsContainer: {
      // display: "none", // Hide cards by default
      "@media (max-width: 768px)": {
        display: "block", // Show cards on small screens
      },
      marginTop: "100px",
    },
    card: {
      border: "1px solid #ddd",
      borderRadius: "8px",
      padding: "16px",
      marginBottom: "16px",
      backgroundColor: "#f9f9f9",
    },
    cardItem: {
      marginBottom: "8px",
    },
  };

  const totals = calculateTotals();

  return (
    <div style={styles.container}>
      <TableHeader
        title=""
        showAddButton={true}
        showBackButton={false}
        onAdd={() => setIsModalOpen(true)}
        showPrintButton={false}
        showExcelButton={false}
        showPdfButton={false}
      />
      <div style={styles.tabContainer}>
        {statusTypes.map((status) => (
          <button
            key={status.id}
            style={{
              ...styles.tab,
              ...(activeStatus === status.id ? styles.activeTab : {}),
            }}
            onClick={() => setActiveStatus(status.id)}
          >
            {status.text}
          </button>
        ))}
      </div>
      <div style={styles.dateFilter}>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Start Date"
          style={styles.dateInput}
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="End Date"
          style={styles.dateInput}
        />
      </div>

      {/* Table for larger screens */}
      {!isMobile && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>DATE</th>
                <th style={styles.th}>TRUCK</th>
                <th style={styles.th}>DRIVER</th>
                <th style={styles.th}>CUSTOMER</th>
                <th style={styles.th}>LOADING POINT</th>
                <th style={styles.th}>VOYAGE NO</th>
                <th style={styles.th}>PRODUCT</th>
                <th style={styles.th}>WAVELINE NO</th>
                <th style={styles.th}>IMAGE</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => {
                return (
                  <tr
                    key={index}
                    onClick={() => setSelectedRow(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.status,
                          ...styles.statusColors[item.StatusID],
                        }}
                      >
                        {item.TripDate.split("T")[0]}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <strong>{item.VregNO.toUpperCase()}</strong>
                    </td>
                    <td style={styles.td}> {item.DriverName.toUpperCase()}</td>
                    <td style={styles.td}>{item.CustomerName.toUpperCase()}</td>
                    <td style={styles.td}>{item.LoadingPointName.toUpperCase()}</td>
                    <td style={styles.td}>{item.ShipmentNo}</td>
                    <td style={styles.td}>
                      {item.CasesFull.toLocaleString() +
                        (item.Trip ? ` -: ${item.Trip}` : "")}
                    </td>
                    <td style={styles.td}>{item.InvoiceNO}</td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <button
                        style={{ ...buttonStyle }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent parent click event
                          // openImageModal(item.imageUrl); // Open image modal
                        }}
                      >
                        Invoice Image
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cards for smaller screens */}
      {isMobile && (
        <div style={styles.cardsContainer}>
          {filteredData?.map((item, index) => {
            return (
              <div
                key={index}
                style={styles.card}
                onClick={() => setSelectedRow(item)}
              >
                {/* Date */}
                <div style={styles.cardItem}>
                  <strong>Date:</strong>{" "}
                  <span
                    style={{
                      ...styles.status,
                      ...styles.statusColors[item.StatusID],
                    }}
                  >
                    {item.TripDate.split("T")[0]}
                  </span>
                </div>

                {/* Truck */}
                <div style={styles.cardItem}>
                  <strong>Truck:</strong>{" "}
                  <strong>{item.VregNO.toUpperCase()}</strong>
                </div>
                {/* LoadingPointName */}
                {/* Driver */}
                <div style={styles.cardItem}>
                  <strong>Driver:</strong> {item.DriverName.toUpperCase()}
                </div>
                <div style={styles.cardItem}>
                  <strong>Loading Point:</strong> {item.LoadingPointName.toUpperCase()}
                </div>

                {/* Customer */}
                <div style={styles.cardItem}>
                  <strong>Customer:</strong> {item.CustomerName.toUpperCase()}
                </div>

                {/* Loader No */}
                <div style={styles.cardItem}>
                  <strong>Loader No:</strong> {item.ShipmentNo}
                </div>

                {/* Product */}
                <div style={styles.cardItem}>
                  <strong>Product:</strong>{" "}
                  {item.CasesFull.toLocaleString() +
                    (item.Trip ? ` -: ${item.Trip}` : "")}
                </div>

                {/* Waveline No */}
                <div style={styles.cardItem}>
                  <strong>Waveline No:</strong> {item.InvoiceNO}
                </div>

                {/* Invoice Image Button */}
                <div style={styles.cardItem}>
                  <button
                    style={buttonStyle}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent parent click event
                      // openImageModal(item.imageUrl); // Open image modal
                    }}
                  >
                    Invoice Image
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isModalOpen && (
        <ShipmentFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(null)}
        />
      )}

      {selectedRow && (
        <ActionModal
          isOpen={selectedRow}
          onClose={() => setSelectedRow(null)}
          shipment={selectedRow}
          fetchOrders={fetchData}
          status={"OFFLOADING"}
        />
      )}
    </div>
  );
};

export default DeliveryTable;

const buttonStyle = {
  background: "#4CAF50",
  border: "none",
  color: "white",
  padding: "5px 10px",
  textAlign: "center",
  textDecoration: "none",
  display: "inline-block",
  fontSize: "14px",
  margin: "4px 2px",
  cursor: "pointer",
  borderRadius: "4px",
  // float: "right",
};